import { useConfirm } from "@/components/DialogProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useConfig, useProjectStatuses } from "@/features/config/hooks/useConfig";
import { useAuth } from "@/contexts/AuthContext";
import type { MobileAppConfig, ProjectModel, ProjectStatus } from "@/firestore_types";
import { createFileRoute } from "@tanstack/react-router";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { updateProject } from "@/features/projects/api/projectService";
import { functions } from "@/firebase";
import { Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { Edit, Loader2, MapPin, Plus, Smartphone, Trash2 } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  isProjectJobLocation,
  getJobLocationAddress,
  normalizeJobLocationForSave,
} from "@/lib/jobLocation";

const getPlacePredictionsCallable = httpsCallable<
  { input: string; sessionToken?: string },
  { suggestions: Array<{ placePrediction?: { placeId: string; text?: { text: string } } }> }
>(functions, "getPlacePredictions");

const getPlaceDetailsCallable = httpsCallable<
  { placeId: string; fields: string[]; sessionToken?: string },
  { formattedAddress?: string; location?: { latitude: number; longitude: number } }
>(functions, "getPlaceDetails");

export const Route = createFileRoute("/dashboard/admin/config/")({
  component: RouteComponent,
});

function RouteComponent() {
  const {
    config,
    loading,
    updating,
    addWorkType,
    removeWorkType,
    addSpecialty,
    removeSpecialty,
    addHoliday,
    updateHoliday,
    removeHoliday,
    updateMobileAppConfig,
  } = useConfig();
  const {
    projectStatuses,
    loading: statusesLoading,
    updating: statusesUpdating,
    addProjectStatus,
    updateProjectStatus,
    removeProjectStatus,
  } = useProjectStatuses();
  const [newWorkType, setNewWorkType] = useState("");
  const [newSpecialty, setNewSpecialty] = useState("");
  const [statusDialog, setStatusDialog] = useState(false);
  const [editingStatus, setEditingStatus] = useState<ProjectStatus | null>(null);
  const [newStatusName, setNewStatusName] = useState("");
  const [newStatusColor, setNewStatusColor] = useState("#3b82f6");
  const [newStatusHideOnProjectList, setNewStatusHideOnProjectList] = useState(false);
  const [newStatusIsDefault, setNewStatusIsDefault] = useState(false);
  const [reminderInputValues, setReminderInputValues] = useState<Record<string, number>>({});
  const { projects: allProjects } = useProjects();

  // Holidays state
  const now = new Date();
  const [holidayYear, setHolidayYear] = useState<number>(now.getFullYear());
  const [holidayMonth, setHolidayMonth] = useState<number>(now.getMonth()); // 0-11
  const [holidayDay, setHolidayDay] = useState<number>(now.getDate());
  const [holidayName, setHolidayName] = useState<string>("");
  const [editingHolidayId, setEditingHolidayId] = useState<string | null>(null);
  const [editingHolidayName, setEditingHolidayName] = useState<string>("");
  const [editingHolidayDay, setEditingHolidayDay] = useState<number>(1);

  const confirm = useConfirm();
  const { userProfile } = useAuth();

  // ── Mobile app kill switch local form state ────────────────────────────────
  const mobileApp = config?.mobileApp;
  const [killSwitchEnabled, setKillSwitchEnabled] = useState<boolean | null>(null);
  const [killSwitchMessage, setKillSwitchMessage] = useState<string | null>(null);
  const [minVersionCode, setMinVersionCode] = useState<string | null>(null);
  const [forceUpdateMessage, setForceUpdateMessage] = useState<string | null>(null);

  // Derive display values: local edits take precedence over Firestore values
  const displayKillSwitch = killSwitchEnabled ?? mobileApp?.killSwitchEnabled ?? false;
  const displayKillSwitchMessage =
    killSwitchMessage !== null ? killSwitchMessage : (mobileApp?.killSwitchMessage ?? "");
  const displayMinVersionCode =
    minVersionCode !== null ? minVersionCode : String(mobileApp?.minVersionCode ?? "");
  const displayForceUpdateMessage =
    forceUpdateMessage !== null
      ? forceUpdateMessage
      : (mobileApp?.forceUpdateMessage ?? "");

  const handleSaveMobileAppConfig = async () => {
    const updates: Partial<MobileAppConfig> = {
      killSwitchEnabled: displayKillSwitch,
      killSwitchMessage: displayKillSwitchMessage.trim() || null,
      minVersionCode: displayMinVersionCode ? parseInt(displayMinVersionCode, 10) || null : null,
      forceUpdateMessage: displayForceUpdateMessage.trim() || null,
    };
    try {
      await updateMobileAppConfig(updates);
      // Reset local state – Firestore will push updated values via snapshot
      setKillSwitchEnabled(null);
      setKillSwitchMessage(null);
      setMinVersionCode(null);
      setForceUpdateMessage(null);
      toast.success("Mobile app config saved");
    } catch {
      toast.error("Failed to save mobile app config");
    }
  };

  // State for updating project locations
  const [updatingProjectIds, setUpdatingProjectIds] = useState<Set<string>>(new Set());
  const [isUpdatingAll, setIsUpdatingAll] = useState(false);

  // Filter projects that have an address but are missing coordinates
  const projectsMissingCoordinates = useMemo(() => {
    return allProjects.filter((project) => {
      if (!isProjectJobLocation(project.jobLocation)) {
        // Legacy string format - has address but no coordinates
        return typeof project.jobLocation === "string" && project.jobLocation.trim() !== "";
      }
      // Object format - check if has address but missing lat/lng
      const loc = project.jobLocation;
      const hasAddress = loc.address && loc.address.trim() !== "";
      const missingCoords = loc.latitude == null || loc.longitude == null;
      return hasAddress && missingCoords;
    });
  }, [allProjects]);

  const handleUpdateProjectLocation = async (project: ProjectModel) => {
    const address = getJobLocationAddress(project.jobLocation);
    if (!address) return;

    setUpdatingProjectIds((prev) => new Set(prev).add(project.id));

    try {
      // Step 1: Get place predictions to find the place_id
      const predictionsResult = await getPlacePredictionsCallable({
        input: address,
      });

      const suggestions = predictionsResult.data.suggestions || [];
      const firstMatch = suggestions.find((s) => s.placePrediction?.placeId);

      if (!firstMatch?.placePrediction?.placeId) {
        toast.error(`No match found for: ${address}`);
        return;
      }

      // Step 2: Get place details with coordinates
      const detailsResult = await getPlaceDetailsCallable({
        placeId: firstMatch.placePrediction.placeId,
        fields: ["formattedAddress", "location", "addressComponents"],
      });

      const location = detailsResult.data.location;
      if (!location) {
        toast.error(`No coordinates found for: ${address}`);
        return;
      }

      // Step 3: Update project with new coordinates
      const currentLocation = isProjectJobLocation(project.jobLocation)
        ? project.jobLocation
        : { address: project.jobLocation as string };

      await updateProject(
        project.id,
        {
          jobLocation: normalizeJobLocationForSave({
            ...currentLocation,
            latitude: location.latitude,
            longitude: location.longitude,
          }),
        },
        userProfile!.uid,
      );

      toast.success(`Updated coordinates for: ${project.name}`);
    } catch (error) {
      console.error("Failed to update location:", error);
      toast.error(`Failed to update: ${project.name}`);
    } finally {
      setUpdatingProjectIds((prev) => {
        const next = new Set(prev);
        next.delete(project.id);
        return next;
      });
    }
  };

  const handleUpdateAllLocations = async () => {
    setIsUpdatingAll(true);
    for (const project of projectsMissingCoordinates) {
      await handleUpdateProjectLocation(project);
    }
    setIsUpdatingAll(false);
  };

  const handleAddWorkType = async () => {
    if (!newWorkType.trim()) return;

    // Check if work type already exists
    if (config?.workTypes.includes(newWorkType.trim())) {
      toast.error("Work type already exists");
      return;
    }

    try {
      await addWorkType(newWorkType.trim());
      setNewWorkType("");
      toast.success("Work type added successfully");
    } catch (error) {
      toast.error("Failed to add work type");
    }
  };

  const handleRemoveWorkType = async (workType: string) => {
    try {
      await removeWorkType(workType);
      toast.success("Work type removed successfully");
    } catch (error) {
      toast.error("Failed to remove work type");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddWorkType();
    }
  };

  const handleAddSpecialty = async () => {
    if (!newSpecialty.trim()) return;

    // Check if specialty already exists
    if (config?.specialties?.includes(newSpecialty.trim())) {
      toast.error("Specialty already exists");
      return;
    }

    try {
      await addSpecialty(newSpecialty.trim());
      setNewSpecialty("");
      toast.success("Specialty added successfully");
    } catch (error) {
      toast.error("Failed to add experience");
    }
  };

  const handleRemoveSpecialty = async (specialty: string) => {
    try {
      await removeSpecialty(specialty);
      toast.success("Specialty removed successfully");
    } catch (error) {
      toast.error("Failed to remove specialty");
    }
  };

  const handleSpecialtyKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddSpecialty();
    }
  };

  const handleAddOrUpdateStatus = async () => {
    if (!newStatusName.trim()) return;

    // Check if status name already exists (case-insensitive)
    const existingStatus = projectStatuses.find(
      (status) =>
        status.name.toLowerCase() === newStatusName.trim().toLowerCase() &&
        (!editingStatus || status.id !== editingStatus.id),
    );

    if (existingStatus) {
      toast.error("Project status name already exists");
      return;
    }

    try {
      const statusData = {
        name: newStatusName.trim(),
        color: newStatusColor,
        hideOnProjectList: newStatusHideOnProjectList,
        isDefault: newStatusIsDefault,
      };

      if (editingStatus) {
        await updateProjectStatus(editingStatus.id, statusData);
        toast.success("Project status updated successfully");
      } else {
        await addProjectStatus(statusData);
        toast.success("Project status added successfully");
      }

      // Reset form
      setNewStatusName("");
      setNewStatusColor("#3b82f6");
      setNewStatusHideOnProjectList(false);
      setNewStatusIsDefault(false);
      setEditingStatus(null);
      setStatusDialog(false);
    } catch (error) {
      toast.error(
        editingStatus ? "Failed to update project status" : "Failed to add project status",
      );
    }
  };

  const handleRemoveStatus = async (status: ProjectStatus) => {
    const confirmed = await confirm({
      title: "Confirm Removal",
      description: `Are you sure you want to remove the project status "${status.name}"? This action cannot be undone.`,
    });
    if (!confirmed) return;
    try {
      await removeProjectStatus(status.id);
      toast.success("Project status removed successfully");
    } catch (error) {
      toast.error("Failed to remove project status");
    }
  };

  const handleEditStatus = (status: ProjectStatus) => {
    setEditingStatus(status);
    setNewStatusName(status.name);
    setNewStatusColor(status.color);
    setNewStatusHideOnProjectList(status.hideOnProjectList || false);
    setNewStatusIsDefault(status.isDefault || false);
    setStatusDialog(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusDialog(false);
    setEditingStatus(null);
    setNewStatusName("");
    setNewStatusColor("#3b82f6");
    setNewStatusHideOnProjectList(false);
    setNewStatusIsDefault(false);
  };

  const timeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const handleUpdateReminder = async (statusId: string, dayThreshold: number, enabled: boolean) => {
    try {
      await updateProjectStatus(statusId, {
        reminderEnabled: enabled,
        dayThreshold,
      });
      toast.success("Reminder settings updated successfully");
    } catch (error) {
      toast.error("Failed to update reminder settings");
    }
  };

  const debouncedUpdateReminder = useCallback(
    (statusId: string, dayThreshold: number, enabled: boolean) => {
      const timeoutKey = `${statusId}-threshold`;

      // Clear existing timeout for this status
      const existingTimeout = timeoutRefs.current.get(timeoutKey);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Set new timeout
      const timeout = setTimeout(() => {
        handleUpdateReminder(statusId, dayThreshold, enabled);
        timeoutRefs.current.delete(timeoutKey);
      }, 1000);

      timeoutRefs.current.set(timeoutKey, timeout);
    },
    [updateProjectStatus],
  );

  if (loading || statusesLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const isUpdating = updating || statusesUpdating;

  return (
    <div className="space-y-6 p-1 sm:p-0">
      <div>
        <h1 className="text-2xl font-bold sm:text-3xl">Configuration</h1>
        <p className="text-muted-foreground">Manage application settings and configurations</p>
        <p className="text-muted-foreground text-xs">Version {APP_VERSION}</p>
      </div>

      <Tabs defaultValue="work-types" className="w-full">
        <div className="-mx-1 overflow-x-auto sm:mx-0">
          <TabsList className="inline-flex h-auto w-full min-w-max justify-start">
            <TabsTrigger value="work-types" className="whitespace-nowrap">
              Work Types
            </TabsTrigger>
            <TabsTrigger value="specialties" className="whitespace-nowrap">
              Specialties
            </TabsTrigger>
            <TabsTrigger value="statuses" className="whitespace-nowrap">
              Project Statuses
            </TabsTrigger>
            <TabsTrigger value="reminders" className="whitespace-nowrap">
              Status Reminders
            </TabsTrigger>
            <TabsTrigger value="holidays" className="whitespace-nowrap">
              Holidays
            </TabsTrigger>
            <TabsTrigger value="mobile-app" className="whitespace-nowrap">
              Mobile App
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="work-types" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Work Types</CardTitle>
              <CardDescription>
                Manage the available work types for projects and workers
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter new work type"
                  value={newWorkType}
                  onChange={(e) => setNewWorkType(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={updating}
                />
                <Button onClick={handleAddWorkType} disabled={updating || !newWorkType.trim()}>
                  {updating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Add
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {config?.workTypes.map((workType) => (
                  <Badge key={workType} variant="secondary" className="flex items-center gap-1">
                    {workType}
                    <button
                      onClick={() => handleRemoveWorkType(workType)}
                      disabled={updating}
                      className="hover:text-destructive ml-1"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>

              {config?.workTypes.length === 0 && (
                <p className="text-muted-foreground py-8 text-center">
                  No work types configured. Add some work types to get started.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="specialties" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Specialties</CardTitle>
              <CardDescription>Manage the available specialties for workers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter new specialty"
                  value={newSpecialty}
                  onChange={(e) => setNewSpecialty(e.target.value)}
                  onKeyPress={handleSpecialtyKeyPress}
                  disabled={updating}
                />
                <Button onClick={handleAddSpecialty} disabled={updating || !newSpecialty.trim()}>
                  {updating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Add
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {config?.specialties?.map((specialty) => (
                  <Badge key={specialty} variant="secondary" className="flex items-center gap-1">
                    {specialty}
                    <button
                      onClick={() => handleRemoveSpecialty(specialty)}
                      disabled={updating}
                      className="hover:text-destructive ml-1"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>

              {(!config?.specialties || config.specialties.length === 0) && (
                <p className="text-muted-foreground py-8 text-center">
                  No specialties configured. Add some specialties to get started.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statuses" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Project Statuses</CardTitle>
              <CardDescription>
                Manage the available project statuses with custom colors
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setStatusDialog(true)} disabled={updating}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Status
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {editingStatus ? "Edit Project Status" : "Add New Project Status"}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="status-name">Status Name</Label>
                        <Input
                          id="status-name"
                          placeholder="Enter status name"
                          value={newStatusName}
                          onChange={(e) => setNewStatusName(e.target.value)}
                          disabled={updating}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="status-color">Status Color</Label>
                        <div className="flex items-center gap-2">
                          <input
                            id="status-color"
                            type="color"
                            value={newStatusColor}
                            onChange={(e) => setNewStatusColor(e.target.value)}
                            disabled={updating}
                            className="h-10 w-12 cursor-pointer rounded border disabled:cursor-not-allowed"
                          />
                          <Input
                            value={newStatusColor}
                            onChange={(e) => setNewStatusColor(e.target.value)}
                            placeholder="#3b82f6"
                            disabled={updating}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between space-x-2">
                        <Label htmlFor="hide-on-list" className="flex flex-col gap-1">
                          <span>Hide status on project list</span>
                          <span className="text-muted-foreground text-xs font-normal">
                            Projects with this status will be hidden by default (e.g., Done,
                            Cancelled)
                          </span>
                        </Label>
                        <Switch
                          id="hide-on-list"
                          checked={newStatusHideOnProjectList}
                          onCheckedChange={setNewStatusHideOnProjectList}
                          disabled={updating}
                        />
                      </div>
                      <div className="flex items-center justify-between space-x-2">
                        <Label htmlFor="is-default" className="flex flex-col gap-1">
                          <span>Set as default status</span>
                          <span className="text-muted-foreground text-xs font-normal">
                            New projects will be created with this status by default
                          </span>
                        </Label>
                        <Switch
                          id="is-default"
                          checked={newStatusIsDefault}
                          onCheckedChange={setNewStatusIsDefault}
                          disabled={updating}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleAddOrUpdateStatus}
                          disabled={updating || !newStatusName.trim()}
                          className="flex-1"
                        >
                          {updating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                          {editingStatus ? "Update" : "Add"} Status
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleCloseStatusDialog}
                          disabled={updating}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="flex flex-wrap gap-2">
                {projectStatuses.map((status) => (
                  <div
                    key={status.id}
                    className="flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium text-white"
                    style={{ backgroundColor: status.color }}
                  >
                    <span>{status.name}</span>
                    {status.isDefault && (
                      <Badge
                        variant="outline"
                        className="ml-1 h-4 border-white/50 px-1 py-0 text-[10px] text-white/90"
                      >
                        Default
                      </Badge>
                    )}
                    <button
                      onClick={() => handleEditStatus(status)}
                      disabled={isUpdating}
                      className="hover:bg-opacity-20 ml-1 rounded p-1 hover:bg-black"
                      title="Edit status"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                    {!allProjects.some((p) => p.statusId === status.id) && (
                      <button
                        onClick={() => handleRemoveStatus(status)}
                        disabled={isUpdating}
                        className="hover:bg-opacity-20 ml-1 rounded p-1 hover:bg-black"
                        title="Remove status"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {projectStatuses.length === 0 && (
                <p className="text-muted-foreground py-8 text-center">
                  No project statuses configured. Add some project statuses to get started.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="holidays" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Holidays</CardTitle>
              <CardDescription>
                Add and manage holidays by year and month. These can be used to adjust schedules or
                calculations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                  {/* Year selector */}
                  <div className="w-full sm:w-32">
                    <Label className="mb-1 block">Year</Label>
                    <Select
                      value={String(holidayYear)}
                      onValueChange={(v) => setHolidayYear(parseInt(v))}
                      disabled={updating}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 7 }).map((_, i) => {
                          const y = now.getFullYear() - 2 + i;
                          return (
                            <SelectItem key={y} value={String(y)}>
                              {y}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Month selector */}
                  <div className="w-full sm:w-40">
                    <Label className="mb-1 block">Month</Label>
                    <Select
                      value={String(holidayMonth)}
                      onValueChange={(v) => setHolidayMonth(parseInt(v))}
                      disabled={updating}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          "January",
                          "February",
                          "March",
                          "April",
                          "May",
                          "June",
                          "July",
                          "August",
                          "September",
                          "October",
                          "November",
                          "December",
                        ].map((m, idx) => (
                          <SelectItem key={m} value={String(idx)}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Day input */}
                  <div className="w-full sm:w-24">
                    <Label className="mb-1 block">Day</Label>
                    <Input
                      type="number"
                      min={1}
                      max={31}
                      value={holidayDay}
                      onChange={(e) =>
                        setHolidayDay(Math.max(1, Math.min(31, Number(e.target.value) || 1)))
                      }
                      disabled={updating}
                    />
                  </div>

                  {/* Name input */}
                  <div className="col-span-2 w-full sm:col-span-1 sm:min-w-56 sm:flex-1">
                    <Label className="mb-1 block">Holiday Name</Label>
                    <Input
                      placeholder="e.g. Independence Day"
                      value={holidayName}
                      onChange={(e) => setHolidayName(e.target.value)}
                      disabled={updating}
                    />
                  </div>
                </div>

                <Button
                  onClick={async () => {
                    if (!holidayName.trim()) {
                      toast.error("Please enter a holiday name");
                      return;
                    }
                    try {
                      const date = new Date(holidayYear, holidayMonth, holidayDay, 12, 0, 0);
                      await addHoliday({
                        name: holidayName.trim(),
                        date: Timestamp.fromDate(date),
                      });
                      toast.success("Holiday added");
                      setHolidayName("");
                    } catch (err) {
                      toast.error(err instanceof Error ? err.message : "Failed to add holiday");
                    }
                  }}
                  disabled={updating}
                  className="w-full sm:w-auto"
                >
                  {updating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-2 h-4 w-4" />
                  )}
                  Add Holiday
                </Button>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">
                  Holidays for{" "}
                  {new Date(holidayYear, holidayMonth).toLocaleString(undefined, {
                    month: "long",
                    year: "numeric",
                  })}
                </Label>
                <div className="divide-y rounded-md border">
                  {(config?.holidays || [])
                    .filter((h) => {
                      const d = h.date.toDate();
                      return d.getFullYear() === holidayYear && d.getMonth() === holidayMonth;
                    })
                    .sort((a, b) => a.date.toMillis() - b.date.toMillis())
                    .map((h) => {
                      const d = h.date.toDate();
                      const isEditing = editingHolidayId === h.id;
                      return (
                        <div
                          key={h.id}
                          className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <div className="bg-muted w-10 shrink-0 rounded py-1 text-center text-sm font-medium">
                              {String(d.getDate()).padStart(2, "0")}
                            </div>
                            {isEditing ? (
                              <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                                <Input
                                  type="number"
                                  min={1}
                                  max={31}
                                  value={editingHolidayDay}
                                  onChange={(e) =>
                                    setEditingHolidayDay(
                                      Math.max(1, Math.min(31, Number(e.target.value) || 1)),
                                    )
                                  }
                                  className="w-20"
                                />
                                <Input
                                  value={editingHolidayName}
                                  onChange={(e) => setEditingHolidayName(e.target.value)}
                                  placeholder="Holiday name"
                                  className="min-w-0 flex-1"
                                />
                              </div>
                            ) : (
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-medium">{h.name}</div>
                                <div className="text-muted-foreground truncate text-xs">
                                  {d.toLocaleDateString(undefined, {
                                    weekday: "short",
                                    year: "numeric",
                                    month: "short",
                                    day: "2-digit",
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {isEditing ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      const newDate = new Date(
                                        holidayYear,
                                        holidayMonth,
                                        editingHolidayDay,
                                        12,
                                        0,
                                        0,
                                      );
                                      await updateHoliday(h.id, {
                                        name: editingHolidayName.trim() || h.name,
                                        date: Timestamp.fromDate(newDate),
                                      });
                                      toast.success("Holiday updated");
                                      setEditingHolidayId(null);
                                    } catch (err) {
                                      toast.error(
                                        err instanceof Error
                                          ? err.message
                                          : "Failed to update holiday",
                                      );
                                    }
                                  }}
                                  disabled={updating}
                                  className="flex-1 sm:flex-none"
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingHolidayId(null)}
                                  className="flex-1 sm:flex-none"
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    const d0 = h.date.toDate();
                                    setEditingHolidayId(h.id);
                                    setEditingHolidayName(h.name);
                                    setEditingHolidayDay(d0.getDate());
                                  }}
                                  className="flex-1 sm:flex-none"
                                >
                                  <Edit className="mr-2 h-4 w-4" /> Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={async () => {
                                    try {
                                      await removeHoliday(h.id);
                                      toast.success("Holiday removed");
                                    } catch (err) {
                                      toast.error(
                                        err instanceof Error
                                          ? err.message
                                          : "Failed to remove holiday",
                                      );
                                    }
                                  }}
                                  disabled={updating}
                                  className="flex-1 sm:flex-none"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  {(!config?.holidays ||
                    (config?.holidays || []).filter((h) => {
                      const d = h.date.toDate();
                      return d.getFullYear() === holidayYear && d.getMonth() === holidayMonth;
                    }).length === 0) && (
                    <div className="text-muted-foreground p-6 text-center text-sm">
                      No holidays for this month.
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reminders" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Project Status Reminders</CardTitle>
              <CardDescription>
                Configure automatic reminders when projects remain in specific statuses for too long
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {projectStatuses.length > 0 ? (
                <div className="space-y-4">
                  {projectStatuses.map((status) => {
                    const isEnabled = status.reminderEnabled || false;
                    const dayThreshold = status.dayThreshold || 7;
                    const inputValue = reminderInputValues[status.id] ?? dayThreshold;

                    return (
                      <div
                        key={status.id}
                        className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="h-4 w-4 shrink-0 rounded-full"
                            style={{ backgroundColor: status.color }}
                          />
                          <span className="font-medium">{status.name}</span>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                          <div className="flex items-center gap-2">
                            <Label htmlFor={`threshold-${status.id}`} className="text-sm">
                              Remind after
                            </Label>
                            <Input
                              id={`threshold-${status.id}`}
                              type="number"
                              min="1"
                              max="365"
                              value={inputValue}
                              onChange={(e) => {
                                const newThreshold = parseInt(e.target.value) || 1;
                                setReminderInputValues((prev) => ({
                                  ...prev,
                                  [status.id]: newThreshold,
                                }));
                                debouncedUpdateReminder(status.id, newThreshold, isEnabled);
                              }}
                              className="w-16 text-center"
                              disabled={isUpdating}
                            />
                            <span className="text-muted-foreground text-sm">days</span>
                          </div>

                          <div className="flex items-center justify-between sm:justify-start">
                            <Label className="text-sm sm:hidden">Enabled</Label>
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={(checked) => {
                                handleUpdateReminder(status.id, inputValue, checked);
                              }}
                              disabled={isUpdating}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground py-8 text-center">
                  No project statuses configured. Add some project statuses first to configure
                  reminders.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Mobile App ──────────────────────────────────────────────────── */}
        <TabsContent value="mobile-app" className="mt-6">
          <div className="space-y-6">
            {/* Kill switch card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  Kill Switch
                </CardTitle>
                <CardDescription>
                  Immediately disable the mobile app for all workers. When enabled, workers see a
                  full-screen message and cannot use the app until you turn this off.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-1">
                    <Label htmlFor="kill-switch-toggle" className="text-base font-medium">
                      Disable mobile app
                    </Label>
                    <p className="text-muted-foreground text-sm">
                      {displayKillSwitch
                        ? "App is currently DISABLED for all workers"
                        : "App is currently available to workers"}
                    </p>
                  </div>
                  <Switch
                    id="kill-switch-toggle"
                    checked={displayKillSwitch}
                    onCheckedChange={(v) => setKillSwitchEnabled(v)}
                    disabled={updating}
                  />
                </div>
                {displayKillSwitch && (
                  <div className="space-y-2">
                    <Label htmlFor="kill-switch-message">Message shown to workers (optional)</Label>
                    <Input
                      id="kill-switch-message"
                      placeholder="e.g. The app is temporarily unavailable. Please check back later."
                      value={displayKillSwitchMessage}
                      onChange={(e) => setKillSwitchMessage(e.target.value)}
                      disabled={updating}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Force update card */}
            <Card>
              <CardHeader>
                <CardTitle>Minimum Version Requirement</CardTitle>
                <CardDescription>
                  Set the minimum Android versionCode (build number) required to use the app.
                  Workers running an older version will see a mandatory update screen.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="min-version-code">Minimum versionCode</Label>
                  <Input
                    id="min-version-code"
                    type="number"
                    min={0}
                    placeholder="e.g. 5  (leave empty or 0 to disable)"
                    value={displayMinVersionCode}
                    onChange={(e) => setMinVersionCode(e.target.value)}
                    disabled={updating}
                    className="max-w-xs"
                  />
                  <p className="text-muted-foreground text-xs">
                    The current app build is <code>versionCode 1</code>. Set a higher number to
                    force workers to update.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="force-update-message">
                    Message shown to workers (optional)
                  </Label>
                  <Input
                    id="force-update-message"
                    placeholder="e.g. A new version of the app is required. Please update from the Play Store."
                    value={displayForceUpdateMessage}
                    onChange={(e) => setForceUpdateMessage(e.target.value)}
                    disabled={updating}
                  />
                </div>
              </CardContent>
            </Card>

            <Button onClick={handleSaveMobileAppConfig} disabled={updating}>
              {updating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save Mobile App Config
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {userProfile?.email === "hamzasunny1772@gmail.com" && (
        <Card className="border-dashed border-yellow-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Badge variant="outline" className="border-yellow-500 text-yellow-600">
                Debug
              </Badge>
              Update Project Coordinates
            </CardTitle>
            <CardDescription>
              Projects with addresses but missing lat/lng coordinates (
              {projectsMissingCoordinates.length} found)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {projectsMissingCoordinates.length > 0 ? (
              <>
                <Button
                  onClick={handleUpdateAllLocations}
                  disabled={isUpdatingAll || updatingProjectIds.size > 0}
                >
                  {isUpdatingAll ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <MapPin className="mr-2 h-4 w-4" />
                  )}
                  Update All ({projectsMissingCoordinates.length})
                </Button>

                <div className="max-h-80 space-y-2 overflow-y-auto rounded-lg border p-2">
                  {projectsMissingCoordinates.map((project) => {
                    const address = getJobLocationAddress(project.jobLocation);
                    const isUpdating = updatingProjectIds.has(project.id);
                    return (
                      <div
                        key={project.id}
                        className="flex items-center justify-between gap-2 rounded-md border p-2"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{project.name}</div>
                          <div className="text-muted-foreground truncate text-xs">{address}</div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateProjectLocation(project)}
                          disabled={isUpdating || isUpdatingAll}
                        >
                          {isUpdating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MapPin className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-muted-foreground py-4 text-center text-sm">
                All projects have coordinates.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
